/**
 * DataTable Component
 * 
 * Generic table component using TanStack Table v8
 * Provides sorting, pagination, row selection, and custom styling
 * 
 * @component
 * @example
 * <DataTable
 *   data={users}
 *   columns={userColumns}
 *   enableSorting
 *   enablePagination
 *   pagination={{ pageIndex: 0, pageSize: 10 }}
 *   totalRows={100}
 * />
 */

import { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  RowSelectionState,
} from '@tanstack/react-table';
import { Loading } from '@components/Common/Loading';
import type { DataTableProps } from './DataTable.types';
import styles from './DataTable.module.css';

const DataTable = <TData,>({
  data,
  columns,
  isLoading = false,
  error = null,
  enableRowSelection = false,
  onRowSelectionChange,
  enableSorting = true,
  initialSorting = [],
  enablePagination = true,
  pagination,
  totalRows,
  onPaginationChange,
  onSortingChange,
  pageSizeOptions = [5, 10, 20, 50, 100],
  defaultPageSize = 10,
  onPageSizeChange,
  showPageSizeSelector = true,
  emptyMessage = 'Nenhum dado disponível',
  className = '',
  showSkeleton = true,
}: DataTableProps<TData>): JSX.Element => {
  // State
  const [sorting, setSorting] = useState(initialSorting);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Default pagination state to prevent undefined errors
  const defaultPagination = { pageIndex: 0, pageSize: defaultPageSize };
  const safePagination = pagination || (enablePagination ? defaultPagination : undefined);

  // Table instance
  const table = useReactTable({
    data: data || [],
    columns: columns || [],
    state: {
      sorting,
      rowSelection,
      ...(enablePagination && { pagination: safePagination }),
    },
    enableRowSelection,
    enableSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      onSortingChange?.(newSorting);
    },
    onRowSelectionChange: setRowSelection,
    manualPagination: !!pagination, // Server-side pagination if pagination prop provided
    pageCount: totalRows && safePagination ? Math.ceil(totalRows / safePagination.pageSize) : undefined,
  });

  // Validate table instance
  if (!table) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>❌ Erro ao inicializar tabela</p>
      </div>
    );
  }

  // Notify parent of selection changes
  useEffect(() => {
    if (enableRowSelection && onRowSelectionChange) {
      const selectedRows = table.getSelectedRowModel()?.rows?.map((row) => row.original) || [];
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, enableRowSelection, onRowSelectionChange, table]);

  // Handle pagination change
  const handlePageChange = (newPageIndex: number) => {
    const currentPagination = pagination || defaultPagination;
    if (onPaginationChange) {
      onPaginationChange({
        ...currentPagination,
        pageIndex: newPageIndex,
      });
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    // Reset to first page when changing page size
    const newPagination = {
      pageIndex: 0,
      pageSize: newPageSize,
    };
    
    if (onPaginationChange) {
      onPaginationChange(newPagination);
    }
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  // Error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>❌ {error}</p>
      </div>
    );
  }

  // Loading state
  if (isLoading && showSkeleton) {
    return (
      <div className={styles.loadingContainer}>
        <Loading type="skeleton" size="md" message="Carregando dados..." />
      </div>
    );
  }

  // Empty state
  if (!isLoading && data.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <p className={styles.emptyMessage}>📋 {emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`${styles.tableContainer} ${className}`}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            {table.getHeaderGroups()?.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`${styles.th} ${
                      header.column.getCanSort() ? styles.sortable : ''
                    }`}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className={styles.headerContent}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className={styles.sortIcon}>
                          {header.column.getIsSorted() === 'asc'
                            ? ' ↑'
                            : header.column.getIsSorted() === 'desc'
                            ? ' ↓'
                            : ' ⇅'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.tbody}>
            {table.getRowModel()?.rows?.map((row) => (
              <tr
                key={row.id}
                className={`${styles.tr} ${row.getIsSelected() ? styles.selected : ''}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={styles.td}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )) || []}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {enablePagination && totalRows && totalRows > 0 && safePagination && (
        <div className={styles.pagination}>
          <div className={styles.paginationLeft}>
            {/* Page Size Selector */}
            {showPageSizeSelector && (
              <div className={styles.pageSizeSelector}>
                <label htmlFor="pageSize" className={styles.pageSizeLabel}>
                  Itens por página:
                </label>
                <select
                  id="pageSize"
                  className={styles.pageSizeSelect}
                  value={safePagination.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Pagination Info */}
            <div className={styles.paginationInfo}>
              Mostrando {safePagination.pageIndex * safePagination.pageSize + 1} a{' '}
              {Math.min((safePagination.pageIndex + 1) * safePagination.pageSize, totalRows)} de{' '}
              {totalRows} registros
            </div>
          </div>
          
          {/* Pagination Controls */}
          <div className={styles.paginationControls}>
            <button
              className={styles.paginationButton}
              onClick={() => handlePageChange(0)}
              disabled={safePagination.pageIndex === 0}
              title="Primeira página"
            >
              ««
            </button>
            <button
              className={styles.paginationButton}
              onClick={() => handlePageChange(safePagination.pageIndex - 1)}
              disabled={safePagination.pageIndex === 0}
              title="Página anterior"
            >
              «
            </button>
            <span className={styles.pageInfo}>
              Página {safePagination.pageIndex + 1} de{' '}
              {Math.ceil(totalRows / safePagination.pageSize)}
            </span>
            <button
              className={styles.paginationButton}
              onClick={() => handlePageChange(safePagination.pageIndex + 1)}
              disabled={safePagination.pageIndex >= Math.ceil(totalRows / safePagination.pageSize) - 1}
              title="Próxima página"
            >
              »
            </button>
            <button
              className={styles.paginationButton}
              onClick={() =>
                handlePageChange(Math.ceil(totalRows / safePagination.pageSize) - 1)
              }
              disabled={safePagination.pageIndex >= Math.ceil(totalRows / safePagination.pageSize) - 1}
              title="Última página"
            >
              »»
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
